import * as z from "zod/mini";
import { proContractorTypeSchema } from "./proPreferences";
import { employmentTypeSchema } from "./shared";

const experienceLevelSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.nullable(z.string()),
});

// Nested read object for the posting's hiring manager (the applicant-notification recipient).
// Null for crawler-mirrored / unassigned postings.
const hiringManagerSchema = z.object({
  id: z.number(),
  first_name: z.nullish(z.string()),
  last_name: z.nullish(z.string()),
});

const jobStatusSchema = z.enum(["open", "filled", "archived"]);
export type JobStatus = z.infer<typeof jobStatusSchema>;

const jobOrderingSchema = z.enum(["-created_ts", "created_ts", "name", "-name"]);

export const jobQueryParamsSchema = z.object({
  ordering: z.optional(jobOrderingSchema),
  search: z.optional(z.string()),
  position_id: z.optional(z.string()),
  status: z.optional(jobStatusSchema),
});
export type JobQueryParams = z.infer<typeof jobQueryParamsSchema>;

const jobSchema = z.object({
  id: z.string(),
  display_id: z.string(),
  name: z.string(),
  wage: z.coerce.number(),
  external_application_link: z.nullish(z.string()),
  status: jobStatusSchema,
  position: z.coerce.number(),
  candidate_count: z._default(z.number(), 0),
  created_ts: z.coerce.date(),
  job_type: proContractorTypeSchema,
  job_type_id: z.coerce.number(),
  experience_level_id: z.coerce.number(),
  experience_level: z.nullish(experienceLevelSchema),
  description: z.string(),
  prospect_notes_instructions: z.nullish(z.string()),
  // New postings default ON (serializer default); existing postings grandfathered OFF (DB default).
  requires_screening_questionnaire: z.boolean(),
  // Read: nested hiring manager object (used to prefill the edit form's manager select).
  hiring_manager: z.nullish(hiringManagerSchema),
  location: z.number(),
  location_details: z.object({
    id: z.number(),
    image_public_id: z.nullish(z.string()),
    name: z.string(),
    market_name: z.string(),
    market_id: z.number(),
    qwick_id: z.nullish(z.number()),
    street_address: z.string(),
    city: z.string(),
    state_code: z.string(),
  }),
});
export const jobPostSchema = z.extend(
  z.omit(jobSchema, {
    id: true,
    display_id: true,
    candidate_count: true,
    created_ts: true,
    job_type: true,
    experience_level: true,
    location_details: true,
  }),
  {
    id: z.optional(z.string()),
    wage: z.coerce.number().check(z.lte(99.99, "Wage cannot exceed $99.99/hr")),
    experience_level_id: z.optional(z.coerce.number()),
    // Write: id of the chosen hiring manager. Optional (not nullable — matches the generated
    // JobPostingRequest, which is `number | undefined`; the API rejects an explicit null). Omitted
    // on submit when unset so the API defaults it to the poster. The nested `hiring_manager` (kept
    // from jobSchema) is read-only server-side, so it's stripped from the submit payload.
    hiring_manager_id: z.optional(z.coerce.number()),
  },
);
export type JobPost = z.infer<typeof jobPostSchema>;

export const applicantFilterQueryParamsSchema = z.object({
  in_network: z.optional(z.boolean()),
  favorites: z.optional(z.boolean()),
  background_check: z.optional(z.boolean()),
  qwick_certified: z.optional(z.boolean()),
  food_handler: z.optional(z.boolean()),
  alcohol_safety: z.optional(z.boolean()),
  has_badges: z.optional(z.boolean()),
  employment_types: z.optional(z.array(employmentTypeSchema)),
});
export type ApplicantFilterQueryParams = z.infer<typeof applicantFilterQueryParamsSchema>;

export const applicantOrderingSchema = z.enum(["-match_score", "-created_ts", "-star_rating", "-completion_rate"]);
export type ApplicantOrdering = z.infer<typeof applicantOrderingSchema>;

// Pros CRM "Candidates" list (GET /api/hire/candidates/, CORE-6087). The spec exposes `ordering`
// as a free string; these are the only documented fields.
export const candidatesOrderingSchema = z.enum([
  "-review_summary__avg_rating",
  "-review_summary__gigs_worked",
  "-review_summary__completion_percentage",
]);
export type CandidatesOrdering = z.infer<typeof candidatesOrderingSchema>;

export const candidatesFilterQueryParamsSchema = z.object({
  favorite_pro: z.optional(z.boolean()),
  top_performer: z.optional(z.boolean()),
  reliable: z.optional(z.boolean()),
  w2: z.optional(z.boolean()),
  lapsed: z.optional(z.boolean()),
});
export type CandidatesFilterQueryParams = z.infer<typeof candidatesFilterQueryParamsSchema>;
